import { isEmpty, isNil } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { UrlActionButtons } from '@shared/components/UrlActionButtons';
import { View } from '@shared/components/View';
import { Link } from '@shared/components/link';
import { MBTextInput } from '@shared/components/textInput';
import { AnchoredTooltip } from '@shared/components/tooltip';
import { TruncateLink } from '@shared/components/truncate';
import { UrlField } from '@shared/data/entity/fields/UrlField';
import { useComponentFocus } from '@shared/scenes/cards/form/components/ComponentFocus';
import { getComponentPlaceholder } from '@shared/scenes/workflows/utils';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { addPrefixIfNeeded, validateUrl } from '@shared/util/validate/url';
import { EditFieldContainer } from '../EditFieldContainer';
import { UrlEditFieldProps } from './UrlEditFieldProps';

export function UrlEditFieldDesktop({
  field,
  component,
  updateField,
  error,
  allowClear,
  style,
}: UrlEditFieldProps) {
  const [value, setValue] = useState(field?.url ?? '');
  const [isFocused, onFocus, onBlur] = useBoolean(false);
  const isInvalid = !validateUrl(value);
  const hasValue = !isEmpty(value);
  const isReadOnly = !!component.readonly;
  const showClearButton = hasValue && !isReadOnly && allowClear;
  // Surface URL *formatting* errors on blur, independent of the form-level `error` prop
  // (which only includes local validation after a submit attempt). Required-field errors
  // still come solely from `error` and remain submit-gated.
  const formatError =
    hasValue && isInvalid ? i18n('workflowTemplate.component.error.url.invalid') : undefined;
  const displayError = error ?? formatError;
  const showError = !isFocused && !isEmpty(displayError);

  const onClear = useCallback(() => {
    setValue('');
  }, []);

  useEffect(() => {
    const updatedField = !isEmpty(value) ? new UrlField([{ url: value.trim() }]) : null;
    updateField(component.id, updatedField);
  }, [value, updateField, component.id]);

  return (
    <EditFieldContainer
      component={component}
      showLabel={true}
      showClearButton={showClearButton}
      onClear={onClear}
      error={showError ? displayError : undefined}
      style={style}
      printContent={field?.url || undefined}
    >
      <View row centerVertically>
        {isReadOnly && !isNil(field) && <ReadOnlyContent field={field} />}
        {!isReadOnly && (
          <EditableContent
            value={value}
            setValue={setValue}
            hasValue={hasValue}
            isInvalid={isInvalid}
            isFocused={isFocused}
            onFocus={onFocus}
            onBlur={onBlur}
            component={component}
            allowClear={allowClear}
          />
        )}
      </View>
    </EditFieldContainer>
  );
}

function EditableContent({
  value,
  setValue,
  hasValue,
  isInvalid,
  isFocused,
  onFocus,
  onBlur,
  component,
  allowClear,
}: {
  value: string;
  setValue: Dispatch<SetStateAction<string>>;
  hasValue: boolean;
  isInvalid: boolean;
  isFocused: boolean;
  onFocus: () => void;
  onBlur: () => void;
  component: API.AnyComponent;
  allowClear?: boolean;
}) {
  const { setComponentFocused, setComponentBlurred } = useComponentFocus();
  const showActionButtons = !isFocused && !isInvalid;

  const handleFocus = useCallback(() => {
    setComponentFocused();
    onFocus();
  }, [setComponentFocused, onFocus]);

  const handleBlur = useCallback(() => {
    setValue(addPrefixIfNeeded);
    setComponentBlurred();
    onBlur();
  }, [setComponentBlurred, onBlur, setValue]);

  const inputStyle = {
    paddingVertical: hasValue ? 0 : grid(1.25),
  };

  const actionButtonsMarginTop = 0;
  const actionButtonsPaddingRight = allowClear ? 0 : grid(2);

  return (
    <>
      <View style={{ flexGrow: 1 }}>
        <MBTextInput
          noBorder
          noPadding
          inputMode="url"
          textContentType="URL"
          keyboardType="url"
          value={value}
          onChangeText={setValue}
          placeholder={getComponentPlaceholder(component)}
          onBlur={handleBlur}
          onFocus={handleFocus}
          autoCapitalize="none"
          style={inputStyle}
        />
      </View>
      {showActionButtons && (
        <MBView
          style={[
            styles.actionButtons,
            { marginTop: actionButtonsMarginTop, paddingRight: actionButtonsPaddingRight },
          ]}
        >
          <UrlActionButtons url={value} />
        </MBView>
      )}
    </>
  );
}

function ReadOnlyContent({ field }: { field: UrlField }) {
  return (
    <View row centerVertically fullWidth>
      <View centerVertically style={{ flexShrink: 1 }}>
        <AnchoredTooltip tooltip={field.url}>
          <Link href={field.url}>
            <TruncateLink href={field.url} />
          </Link>
        </AnchoredTooltip>
      </View>
      <UrlActionButtons url={field.url} showOpen={false} style={{ marginLeft: grid(1) }} />
    </View>
  );
}

const styles = StyleSheet.create({
  actionButtons: {
    flexGrow: 0,
    flexShrink: 1,
    paddingLeft: grid(2),
  },
});
